import { Search, ArrowUp, Download, PanelLeft, Megaphone, LogIn, MessageCircle, ShieldCheck, ClipboardList, ExternalLink, Compass, ChevronRight, Users, GraduationCap, Briefcase, Building2, Link2, FolderOpen, BookOpen, Scale } from "lucide-react";
import { ExternalResources } from "@/components/ExternalResources";
import { Button } from "@/components/ui/button";
import type { WelcomeBanner, DecisionTree } from "@/types/cms";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface PMResourceLink {
  id: string;
  label: string;
  folderLabel?: string;
}

interface WelcomePageProps {
  onGetStarted: () => void;
  onSearchFocus?: () => void;
  onFindDownloads?: () => void;
  onToggleSidebar?: () => void;
  onNavigate?: (path: string) => void;
  onSelectTree?: (tree: DecisionTree) => void;
  onSelectTab?: (tabId: string) => void;
  onSelectFolder?: (folderSlug: string) => void;
  decisionTrees?: DecisionTree[];
  pmResources?: PMResourceLink[];
  banner?: WelcomeBanner | null;
  isAuthenticated?: boolean;
  onOpenLearningProgram?: () => void;
  onOpenNera?: () => void;
  // Tab IDs for the two anchor documents (Handbook + NTCER) so welcome-page
  // tiles can navigate to them by slug-resolved id. Computed in Home.tsx.
  handbookTabId?: string;
  ntcerTabId?: string;
}

// Folder slugs matched to the "In this hub" boxes. Must agree with
// SidebarNavigation's TPA_RESOURCES_SLUG / PM_FOLDER_SLUGS.
const TPA_RESOURCES_SLUG = 'folder-1771484890761';
const PM_LANDING_FOLDER_SLUG = 'resources-pm';

const features = [
  {
    icon: PanelLeft,
    title: "Browse by topic",
    description:
      "Open the sidebar to explore by section. You can collapse it any time using the panel icon in the top-left corner, or press Ctrl+B.",
    action: "sidebar" as const,
    buttonLabel: "Open sidebar",
  },
  {
    icon: Search,
    title: "Search for anything",
    description:
      "Looking for something specific? Use the search bar in the top-right corner to find content across every section instantly.",
    action: "search" as const,
    buttonLabel: "Search now",
  },
  {
    icon: Download,
    title: "Download resources",
    description:
      "Where a downloadable document is available, you'll find a download button near the top of that section.",
    action: "downloads" as const,
    buttonLabel: "Find downloads",
  },
  {
    icon: ArrowUp,
    title: "Jump back to top",
    description:
      "Scroll down and an orange arrow button appears in the bottom-right corner. Tap it to jump straight back to the top.",
    action: "getstarted" as const,
    buttonLabel: "Get started",
  },
];

const FEEDBACK_PATH_PUBLIC = "/feedback/resources-review-202602";
const FEEDBACK_PATH_TPA = "/feedback/tpa-review-202602";
const FEEDBACK_PATH_PLACEMENT = "/feedback/placement-process-202604";

interface TeamTool {
  icon: typeof MessageCircle;
  title: string;
  description: string;
  url?: string;
  disabled?: boolean;
}

// Internal tools (no URL - informational / in-app navigation)
const internalTools: TeamTool[] = [
  {
    icon: MessageCircle,
    title: "Nera AI Assistant",
    description: "Ask Nera anything about registrar training, NTCER, onboarding, or practice management. Look for the chat icon in the bottom-right corner.",
  },
  {
    icon: ShieldCheck,
    title: "TPA Resources",
    description: "Guides, compliance checklists, and operational resources tailored to your role. Open TPA Resources in the sidebar.",
  },
  {
    icon: BookOpen,
    title: "Training Post Handbook",
    description: "The full ACRRM Training Post Handbook 2026 — vision, accreditation, employment, placement, supervision, and quality education in one place.",
  },
  {
    icon: FolderOpen,
    title: "Training Post Resources",
    description: "All PM-facing checklists, references, and downloads — consolidated under one heading in the sidebar.",
  },
  {
    icon: Scale,
    title: "NTCER",
    description: "National Terms and Conditions for the Employment of Registrars (v2026.2). Pay rates, leave, super, schedules, signatures.",
  },
  {
    icon: GraduationCap,
    title: "Learning Program",
    description: "Your TPA microlearning modules. Self-paced, six modules covering practice team engagement, NTCER, multi-pathway, and more.",
  },
];

// External tools (launch in new tab). URLs are placeholders — update when real URLs are available.
const externalTools: TeamTool[] = [
  {
    icon: Briefcase,
    title: "Aptify",
    description: "ACRRM member and CRM system. Registrar records, placements, contact logs.",
    url: "https://acrrm.aptify.com",
    disabled: true, // URL placeholder - not yet confirmed
  },
  {
    icon: Users,
    title: "Employee Connect",
    description: "ACRRM employee portal for HR, leave, payroll, and internal communications.",
    url: "",
    disabled: true, // URL to be confirmed
  },
  {
    icon: Building2,
    title: "RG Hub",
    description: "Training post accreditation, registrar profiles, capacity collection.",
    url: "https://rghub.acrrm.org.au",
    disabled: true, // URL placeholder - not yet confirmed
  },
  {
    icon: Link2,
    title: "SharePoint",
    description: "ACRRM SharePoint for internal documents, team sites, and shared resources.",
    url: "",
    disabled: true, // URL to be confirmed
  },
];

export function WelcomePage({ onGetStarted, onSearchFocus, onFindDownloads, onToggleSidebar, onNavigate, onSelectTree, onSelectTab, onSelectFolder, decisionTrees, pmResources, banner, isAuthenticated, onOpenLearningProgram, onOpenNera, handbookTabId, ntcerTabId }: WelcomePageProps) {

  const visibleTrees = (decisionTrees || []).filter(t => t.is_visible && (!t.requires_auth || isAuthenticated));

  const handleFeatureClick = (action: string) => {
    switch (action) {
      case "sidebar":
        onToggleSidebar?.();
        break;
      case "search":
        onSearchFocus?.();
        break;
      case "downloads":
        onFindDownloads?.();
        break;
      default:
        onGetStarted();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] px-4 py-12 md:py-16">
      {/* Banner */}
      {banner?.enabled && banner.content && (
        <div className="max-w-3xl w-full mb-8 rounded-xl border border-[var(--color-acrrm-orange)]/30 bg-[var(--color-acrrm-orange)]/5 p-5">
          <div className="flex gap-3">
            <Megaphone className="h-5 w-5 text-[var(--color-acrrm-orange)] flex-shrink-0 mt-0.5" />
            <div className="prose prose-sm prose-stone max-w-none [&_p]:m-0 [&_p+p]:mt-2 [&_a]:text-[var(--color-acrrm-orange)] [&_strong]:text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {banner.content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="max-w-2xl text-center space-y-4 mb-10">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground leading-tight">
          Welcome to the ACRRM Training&nbsp;Post Resource&nbsp;Hub
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {isAuthenticated
            ? "Everything you need to support training posts in their role with ACRRM registrars — operational resources, online modules and quick access to your work platforms, all in one place."
            : "Everything you need to support GP registrar training at your practice — handbooks, regulatory references, and key resources, all in one place."}
        </p>
      </div>

      {/* Authenticated: Your Team Tools */}
      {isAuthenticated && (
        <div className="max-w-5xl w-full mb-10">
          <div className="rounded-xl border-2 border-[var(--color-acrrm-dark)]/20 bg-[var(--color-acrrm-dark)]/[0.03] p-6 md:p-8">
            <div className="text-center mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                Your Team Tools
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                You're signed in &mdash; here's what's available to you.
              </p>
            </div>

            {/* In-hub tools */}
            <div className="mb-5">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">In this hub</p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {internalTools.map(({ icon: Icon, title, description }) => {
                  // Wire each box to an appropriate click action. Section boxes
                  // now navigate to the folder/section heading in the sidebar
                  // (not a specific resource inside it).
                  let onClick: (() => void) | undefined;
                  if (title === "Nera AI Assistant") onClick = onOpenNera;
                  else if (title === "TPA Resources") onClick = () => onSelectFolder?.(TPA_RESOURCES_SLUG);
                  else if (title === "Training Post Handbook" && handbookTabId) onClick = () => onSelectTab?.(handbookTabId);
                  else if (title === "Training Post Resources") onClick = () => onSelectFolder?.(PM_LANDING_FOLDER_SLUG);
                  else if (title === "NTCER" && ntcerTabId) onClick = () => onSelectTab?.(ntcerTabId);
                  else if (title === "Learning Program") onClick = onOpenLearningProgram;

                  const isClickable = !!onClick;
                  const commonClass = "flex flex-col p-4 rounded-lg border bg-card shadow-sm text-left";
                  const hoverClass = isClickable
                    ? "hover:border-[var(--color-acrrm-dark)]/40 hover:shadow-md transition-all group cursor-pointer"
                    : "";

                  return isClickable ? (
                    <button key={title} onClick={onClick} className={`${commonClass} ${hoverClass}`}>
                      <div className="h-10 w-10 rounded-lg bg-[var(--color-acrrm-dark)]/10 flex items-center justify-center mb-3 group-hover:bg-[var(--color-acrrm-dark)]/20 transition-colors">
                        <Icon className="h-5 w-5 text-[var(--color-acrrm-dark)]" />
                      </div>
                      <h3 className="font-heading font-semibold text-foreground text-sm mb-1">
                        {title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    </button>
                  ) : (
                    <div key={title} className={commonClass}>
                      <div className="h-10 w-10 rounded-lg bg-[var(--color-acrrm-dark)]/10 flex items-center justify-center mb-3">
                        <Icon className="h-5 w-5 text-[var(--color-acrrm-dark)]" />
                      </div>
                      <h3 className="font-heading font-semibold text-foreground text-sm mb-1">
                        {title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* External tool launchpad */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Your work tools</p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {externalTools.map(({ icon: Icon, title, description, url, disabled }) => {
                  const isLive = !disabled && url;
                  return isLive ? (
                    <a
                      key={title}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col p-4 rounded-lg border bg-card shadow-sm hover:border-[var(--color-acrrm-dark)]/40 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="h-10 w-10 rounded-lg bg-[var(--color-acrrm-dark)]/10 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-[var(--color-acrrm-dark)]" />
                        </div>
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <h3 className="font-heading font-semibold text-foreground text-sm mb-1">
                        {title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    </a>
                  ) : (
                    <div
                      key={title}
                      className="flex flex-col p-4 rounded-lg border border-dashed bg-muted/20 opacity-60"
                      title="Link coming soon"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">soon</span>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground text-sm mb-1">
                        {title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Anchor documents — Handbook + NTCER (visible to all; both authed + public). */}
      {/* Authenticated users also see these in "In this hub" above; the dedicated  */}
      {/* hero pair gives them prominence as the two most-referenced source docs.    */}
      {!isAuthenticated && (handbookTabId || ntcerTabId) && (
        <div className="grid gap-4 sm:grid-cols-2 max-w-3xl w-full mb-10">
          {handbookTabId && (
            <button
              onClick={() => onSelectTab?.(handbookTabId)}
              className="flex gap-4 p-5 rounded-xl border-2 border-[var(--color-acrrm-dark)]/25 bg-[var(--color-acrrm-dark)]/[0.04] text-left transition-all hover:border-[var(--color-acrrm-dark)]/50 hover:bg-[var(--color-acrrm-dark)]/[0.08] hover:shadow-md group"
            >
              <div className="flex-shrink-0 h-11 w-11 rounded-lg bg-[var(--color-acrrm-dark)]/10 flex items-center justify-center group-hover:bg-[var(--color-acrrm-dark)]/20 transition-colors">
                <BookOpen className="h-6 w-6 text-[var(--color-acrrm-dark)]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-foreground mb-1">
                  Training Post Handbook
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The full ACRRM Training Post Handbook 2026 — vision, accreditation, employment, placement, supervision, quality education.
                </p>
              </div>
            </button>
          )}
          {ntcerTabId && (
            <button
              onClick={() => onSelectTab?.(ntcerTabId)}
              className="flex gap-4 p-5 rounded-xl border-2 border-[var(--color-acrrm-dark)]/25 bg-[var(--color-acrrm-dark)]/[0.04] text-left transition-all hover:border-[var(--color-acrrm-dark)]/50 hover:bg-[var(--color-acrrm-dark)]/[0.08] hover:shadow-md group"
            >
              <div className="flex-shrink-0 h-11 w-11 rounded-lg bg-[var(--color-acrrm-dark)]/10 flex items-center justify-center group-hover:bg-[var(--color-acrrm-dark)]/20 transition-colors">
                <Scale className="h-6 w-6 text-[var(--color-acrrm-dark)]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-foreground mb-1">
                  NTCER
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  National Terms and Conditions for the Employment of Registrars (v2026.2). Pay rates, leave, super, schedules.
                </p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Feature grid — actionable cards */}
      <div className="grid gap-6 sm:grid-cols-2 max-w-3xl w-full mb-12">
        {features.map(({ icon: Icon, title, description, action, buttonLabel }) => (
          <button
            key={title}
            onClick={() => handleFeatureClick(action)}
            className="flex gap-4 p-5 rounded-xl border bg-card shadow-sm text-left transition-colors hover:border-[var(--color-acrrm-orange)]/40 hover:shadow-md group"
          >
            <div className="flex-shrink-0 flex items-start pt-0.5">
              <div className="h-10 w-10 rounded-lg bg-[var(--color-acrrm-orange)]/10 flex items-center justify-center group-hover:bg-[var(--color-acrrm-orange)]/20 transition-colors">
                <Icon className="h-5 w-5 text-[var(--color-acrrm-orange)]" />
              </div>
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-1">
                {title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                {description}
              </p>
              <span className="text-xs font-medium text-[var(--color-acrrm-orange)] group-hover:underline">
                {buttonLabel} &rarr;
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Interactive Guides — public welcome page only (TPAs access via sidebar) */}
      {!isAuthenticated && visibleTrees.length > 0 && (
        <div className="max-w-3xl w-full mb-12">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 mb-2">
              <Compass className="h-5 w-5 text-[var(--color-acrrm-orange)]" />
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                Interactive Guides
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Step-by-step walkthroughs with screenshots — answer a few questions and get exactly the help you need.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-1">
            {visibleTrees.map(tree => (
              <button
                key={tree.id}
                onClick={() => onSelectTree?.(tree)}
                className="flex items-center gap-4 p-5 rounded-xl border-2 border-[var(--color-acrrm-orange)]/20 bg-[var(--color-acrrm-orange)]/[0.03] text-left transition-all hover:border-[var(--color-acrrm-orange)]/50 hover:shadow-md hover:bg-[var(--color-acrrm-orange)]/[0.06] group"
              >
                <span className="text-2xl flex-shrink-0">{tree.icon}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-foreground mb-0.5">
                    {tree.title}
                  </h3>
                  {tree.description && (
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                      {tree.description}
                    </p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-[var(--color-acrrm-orange)] transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ACRRM External Resources — public welcome page only (TPAs access via sidebar) */}
      {!isAuthenticated && <ExternalResources />}

      {/* Placement Process Feedback — public (PM) welcome page only */}
      {!isAuthenticated && (
        <button
          onClick={() => onNavigate?.(FEEDBACK_PATH_PLACEMENT)}
          className="max-w-3xl w-full mb-6 rounded-xl border-2 border-[var(--color-acrrm-dark)]/30 bg-[var(--color-acrrm-dark)]/[0.04] p-5 md:p-6 text-left transition-colors hover:border-[var(--color-acrrm-dark)]/50 hover:bg-[var(--color-acrrm-dark)]/[0.07] group"
        >
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-[var(--color-acrrm-dark)]/10 flex items-center justify-center group-hover:bg-[var(--color-acrrm-dark)]/20 transition-colors">
              <Users className="h-5 w-5 text-[var(--color-acrrm-dark)]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-foreground mb-1">
                Tell us about your placement experience
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                We are gathering feedback from practice managers about the ACRRM registrar placement process &mdash; what works well, what could be simpler, and what would make your life easier. Share your experience through a short, anonymous conversation with Nera.
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-acrrm-dark)] group-hover:underline">
                Share your placement experience &rarr;
              </span>
            </div>
          </div>
        </button>
      )}

      {/* Content Review / Feedback invite — visible to all */}
      <button
        onClick={() => onNavigate?.(isAuthenticated ? FEEDBACK_PATH_TPA : FEEDBACK_PATH_PUBLIC)}
        className="max-w-3xl w-full mb-12 rounded-xl border-2 border-dashed border-[var(--color-acrrm-orange)]/30 bg-[var(--color-acrrm-orange)]/[0.03] p-5 md:p-6 text-left transition-colors hover:border-[var(--color-acrrm-orange)]/50 hover:bg-[var(--color-acrrm-orange)]/[0.06] group"
      >
        <div className="flex gap-4 items-start">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-[var(--color-acrrm-orange)]/10 flex items-center justify-center group-hover:bg-[var(--color-acrrm-orange)]/20 transition-colors">
            <ClipboardList className="h-5 w-5 text-[var(--color-acrrm-orange)]" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-foreground mb-1">
              Help us improve these resources
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-2">
              Share your feedback through a short, guided conversation with Nera. It's anonymous, takes just a few minutes, and directly shapes future updates to this hub.
            </p>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-acrrm-orange)] group-hover:underline">
              Start feedback conversation <ExternalLink className="h-3 w-3" />
            </span>
          </div>
        </div>
      </button>

      {/* CTA */}
      <Button
        size="lg"
        onClick={onGetStarted}
        className="gap-2 text-base px-8 bg-[var(--color-acrrm-orange)] hover:bg-[var(--color-acrrm-orange-hover)] text-white"
      >
        Get started
      </Button>

      {/* Subtle Team Login link for unauthenticated visitors */}
      {!isAuthenticated ? (
        <button
          onClick={() => onNavigate?.("/login")}
          className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogIn className="h-3.5 w-3.5" />
          ACRRM team member? Sign in for additional tools
        </button>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground">
          Open the sidebar to choose a section, or ask Nera using the chat icon
          in the bottom-right corner.
        </p>
      )}
    </div>
  );
}
